/**
 * Zoom for the depth chart diamond.
 *
 * The zoom MULTIPLIES a fit the chart works out for itself. The field is drawn at its 1400x800
 * design size and scaled by fit = (the chart's measured width / 1400), so at zoom 1 it exactly
 * fills the container's width, text and all (ScholarshipDepthChartTab, `--dc-fit` in
 * depthChart.css). Before the width is measured (and in jsdom) fit is absent and the diamond is
 * sized by percentages at `width: 100%`, which also fills the width at zoom 1.
 *
 * So zoom runs in both directions from a default of 1, relative to that fit:
 *
 *   - **Above 1** enlarges the whole chart past the container's width, and the container
 *     scrolls. This is for a coach reading a crowded position.
 *   - **Below 1** shrinks it below the fit. Useful on a wide monitor, where the fitted field is
 *     tall enough that the foot of the diamond falls below the fold.
 *
 * The bounds apply to the zoom, on top of the fit: ZOOM_MIN is 70% of whatever the fit is, not an
 * absolute scale. A chart too narrow to read even at fit opens as a list instead (see
 * depthChartLayout), so there is no zoom floor tied to the screen.
 *
 * Text scales because the diamond is transformed rather than widened. Widening it would leave
 * font sizes fixed, so cards would get narrower without getting more readable — the opposite of
 * what a zoom control is for.
 */

/** The smallest zoom, as a fraction of the fit: below it the cards stop being worth the field. */
export const ZOOM_MIN = 0.7;

/** The largest zoom, as a multiple of the fit: past it a single card fills the viewport and the
 * coach has lost the diamond. */
export const ZOOM_MAX = 2;

export const ZOOM_STEP = 0.1;

/** The default zoom: the fit alone, where the chart exactly fills the width of its container. */
export const ZOOM_FIT = 1;

/**
 * Rounds to two decimals. Stepping by 0.1 in binary floating point drifts
 * (0.7 + 0.1 + 0.1 = 0.8999999999999999), which would render as a 90% readout and never compare
 * equal to ZOOM_FIT again.
 */
const round2 = (n: number): number => Math.round(n * 100) / 100;

export const clampZoom = (zoom: number): number => {
  if (!Number.isFinite(zoom)) return ZOOM_FIT;
  return round2(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)));
};

/** One notch in or out, never past the bounds. */
export const stepZoom = (zoom: number, direction: 'in' | 'out'): number =>
  clampZoom(zoom + (direction === 'in' ? ZOOM_STEP : -ZOOM_STEP));

export const canZoomIn = (zoom: number): boolean => clampZoom(zoom) < ZOOM_MAX;
export const canZoomOut = (zoom: number): boolean => clampZoom(zoom) > ZOOM_MIN;

/** The readout, e.g. 1.1 -> "110%". */
export const zoomPercentLabel = (zoom: number): string => `${Math.round(clampZoom(zoom) * 100)}%`;
